import { useRef } from 'react';

type AutoFocusHandler = (event: Event) => void;

// A dropdown content labels itself with its trigger's id, so the trigger can be found from the
// closing content. Context menus have no trigger; they fall back to the element that was focused
// when the menu opened, which is what Radix itself would restore.
const resolveFocusTarget = (event: Event, opener: HTMLElement | null): HTMLElement | null => {
    const content = event.currentTarget ?? event.target;

    if (!(content instanceof HTMLElement)) {
        return opener;
    }

    const triggerId = content.getAttribute('aria-labelledby');

    return (triggerId === null ? null : content.ownerDocument.getElementById(triggerId)) ?? opener;
};

// Radix menus refocus their trigger on close with a plain focus(), which the browser answers by
// scrolling the trigger into view. That scroll lands in the same frame as the tiling engine's
// smooth scroll toward a tile the menu item just opened and cancels it. Restoring the same focus
// target with preventScroll keeps keyboard focus where Radix would put it without moving the surface.
export const restoreFocusWithoutScroll = (event: Event, opener: HTMLElement | null): void => {
    if (event.defaultPrevented) {
        return;
    }

    const target = resolveFocusTarget(event, opener);

    if (target === null || !target.isConnected) {
        return;
    }

    event.preventDefault();
    target.focus({ preventScroll: true });
};

// The close event fires on the unmounting menu content, so the opener has to be captured when the
// menu opens. Radix has not moved focus yet at that point. A close caused by interacting outside
// the menu is left to Radix: a non-modal menu must not pull focus away from the control the user
// just clicked.
export const useRestoreMenuFocusWithoutScroll = <TOutsideEvent extends Event>({
    onCloseAutoFocus,
    onInteractOutside,
    onOpenAutoFocus,
}: {
    readonly onCloseAutoFocus?: AutoFocusHandler;
    readonly onInteractOutside?: (event: TOutsideEvent) => void;
    readonly onOpenAutoFocus?: AutoFocusHandler;
}): {
    readonly onCloseAutoFocus: AutoFocusHandler;
    readonly onInteractOutside: (event: TOutsideEvent) => void;
    readonly onOpenAutoFocus: AutoFocusHandler;
} => {
    const openerRef = useRef<HTMLElement | null>(null);
    const hasInteractedOutsideRef = useRef(false);

    return {
        onCloseAutoFocus: (event) => {
            onCloseAutoFocus?.(event);
            if (!hasInteractedOutsideRef.current) {
                restoreFocusWithoutScroll(event, openerRef.current);
            }
            hasInteractedOutsideRef.current = false;
        },
        onInteractOutside: (event) => {
            onInteractOutside?.(event);
            hasInteractedOutsideRef.current = true;
        },
        onOpenAutoFocus: (event) => {
            openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            hasInteractedOutsideRef.current = false;
            onOpenAutoFocus?.(event);
        },
    };
};
